import React, { useEffect } from 'react';
import { graphql, useFragment } from 'react-relay';
import { fg } from '@atlassian/jira-feature-gating';
import { TabsSkeleton } from '@atlassian/jira-horizontal-nav-common/src/ui/horizontal-nav-skeleton/index.tsx';
import { useIsFullscreen } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/fullscreen/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useSetMostRecentlyViewedBoard } from '@atlassian/jira-horizontal-nav-common/src/services/set-most-recently-viewed-board/useSetMostRecentlyViewedBoard.tsx';
import type { JiraHorizontalNavigationContentOld$key } from '@atlassian/jira-relay/src/__generated__/JiraHorizontalNavigationContentOld.graphql';
import TabsContent from './TabsContent.tsx';
import NavWrapper, { callWhenIdle } from './NavWrapper.tsx';
import NavHeader from './header/NavHeader.tsx';
import { CustomTemplateProjectBanner } from './CustomTemplateProjectBanner.tsx';

export const JiraHorizontalNavigationContentOld = ({
	queryRef,
}: {
	queryRef: JiraHorizontalNavigationContentOld$key;
}): React.JSX.Element | null => {
	const data = useFragment<JiraHorizontalNavigationContentOld$key>(
		graphql`
			fragment JiraHorizontalNavigationContentOld on Query
			@argumentDefinitions(
				isJiraCustomTemplatesUxEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/jira-custom-templates-ux-fg.relayprovider"
				}
			) {
				...TabsContent_query
				...NavHeader_query
				jira_projectByIdOrKey(cloudId: $cloudId, idOrKey: $projectIdOrKey)
					@include(if: $withProject) {
					...TabsContent_project
					...NavHeader_project
					...CustomTemplateProjectBanner @include(if: $isJiraCustomTemplatesUxEnabled)
					softwareBoardCount
					__id
				}
				jira {
					containerNavigation(input: $containerNavigationInput)
						@required(action: THROW)
						@optIn(to: "JiraContainerNavigation") {
						... on JiraContainerNavigation {
							scopeId
						}
						... on QueryError {
							extensions {
								statusCode
							}
						}
					}
				}
			}
		`,
		queryRef,
	);

	const project = data.jira_projectByIdOrKey;

	const [isFullscreen] = useIsFullscreen();

	const setMostRecentlyViewedBoard = useSetMostRecentlyViewedBoard();

	useEffect(() => {
		if (fg('jfp-magma-concurrent-board-perf-3')) {
			callWhenIdle(() => {
				if (
					data?.jira?.containerNavigation?.scopeId &&
					project?.softwareBoardCount &&
					project.softwareBoardCount > 0
				) {
					setMostRecentlyViewedBoard?.(data.jira.containerNavigation.scopeId, project?.__id);
				}
			});
		} else if (
			data?.jira?.containerNavigation?.scopeId &&
			project?.softwareBoardCount &&
			project.softwareBoardCount > 0
		) {
			setMostRecentlyViewedBoard?.(data.jira.containerNavigation.scopeId, project?.__id);
		}
	}, [
		setMostRecentlyViewedBoard,
		data?.jira?.containerNavigation?.scopeId,
		project?.__id,
		project?.softwareBoardCount,
	]);

	// Some routes with invalid project scope or scope Id request for horizontal navigation
	// E.g. JSM projects do not support horizontal navigation, but connect app routes in JSM will request for it
	// These cases are unprocessable and return a dedicated 422 error, so we can skip rendering and return null
	if (data.jira?.containerNavigation?.extensions?.[0]?.statusCode === 422) {
		return null;
	}

	return (
		<>
			{project && fg('jira_custom_templates_ux') && (
				<CustomTemplateProjectBanner projectFragment={project} />
			)}
			<NavWrapper>
				<NavHeader projectFragment={project} queryFragment={data} />
				<JSErrorBoundary
					id="HorizontalNavTabContentErrorBoundary"
					packageName="jiraHorizontalNav"
					fallback={TabsSkeleton}
				>
					{!isFullscreen ? <TabsContent projectFragment={project} queryFragment={data} /> : null}
				</JSErrorBoundary>
			</NavWrapper>
		</>
	);
};
